import React from 'react';
import { useParams, useNavigate  } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Container,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ArrowBack, ExpandMore } from '@mui/icons-material';

const serviceDetails = {
    1: {
        title: 'Infor M3 Software Services',
        image: '/images/sap-integration.jpg',
        gallery: [
            '/assets/working-1.jpg',
            '/assets/working-2.jpg',
            '/assets/working-3.jpg',
            '/assets/working-4.jpg',
        ],
        points: [
            {
                title: 'Infor M3 Implementation',
                detail: 'Infor M3 Implementation',
                subpoints: [
                  'Full-cycle implementation of Infor M3 across various industries',
                  'Process mapping, configuration, and deployment tailored to your business needs',
                  'Project management and change management support'
                ]
            },
            {
                title: 'Infor M3 Upgrade & Migration',
                detail: 'Infor M3 Upgrade & Migration',
                subpoints: [
                  'Upgrades from older M3 versions to the latest cloud or on-premise release',
                  'Data migration planning, cleansing, and validation',
                  'System performance tuning during and after migration'
                ]
            },
            {
                title: 'Infor M3 Integration Services',
                detail: 'Infor M3 Integration Services',
                subpoints: [
                  'Integration with third-party systems like CRMs, WMS, PLMs, and financial software',
                  'API development and middleware solutions (Infor ION, Mongoose)',
                  'Real-time data visibility and cross-platform workflow automation'
                ]
            },
            {
                title: 'Infor M3 Customization & Development',
                detail: 'Infor M3 Customization & Development',
                subpoints: [
                  'Extension and customization of M3 to meet unique business needs',
                  'Development of custom reports, forms, and dashboards',
                  'Business logic enhancements using Infor development tools'
                ]
            },
            {
                title: 'Infor M3 Managed Services & Support',
                detail: 'Infor M3 Managed Services & Support',
                subpoints: [
                  'Ongoing support including incident management, patches, and updates',
                  'System monitoring, performance analysis, and proactive maintenance',
                  'SLA-based support with a dedicated team of experts'
                ]
            },
            {
                title: 'Infor M3 Training & User Enablement',
                detail: 'Infor M3 Training & User Enablement',
                subpoints: [
                  'Role-based user training for finance, supply chain, manufacturing, and admin teams',
                  'Documentation and knowledge transfer for internal IT and end-users',
                  'Change management assistance to ensure user adoption'
                ]
            }
        ],
      },
    2: {
        title: 'SAP Implementation Services',
        image: '/images/sap-implementation.jpg',
        gallery: [
            '/assets/services.jpg',
            '/assets/services-1.jpg',
            '/assets/services-2.jpg',
            '/assets/services-3.jpg',
        ],
        points: [
            {
                title: 'SAP Implementation Services',
                detail: 'SAP Implementation Services',
                subpoints: [
                'End-to-end implementation of SAP ERP modules (SAP S/4HANA, SAP ECC, SAP FICO, SAP MM, SAP SD, etc.)',
                'Tailored solutions aligned with your business processes and industry standards',
                'Data migration, system configuration, and user training'
                ]
            },
            {
                title: 'SAP Integration Services',
                detail: 'SAP Integration Services',
                subpoints: [
                'Seamless integration of SAP with third-party applications and platforms',
                'Real-time data synchronization and API development',
                'SAP PI/PO, SAP CPI, and other middleware solutions'
                ]
            },
            {
                title: 'SAP Upgrade & Migration',
                detail: 'SAP Upgrade & Migration',
                subpoints: [
                'System upgrades to the latest SAP versions',
                'Migration from SAP ECC to SAP S/4HANA',
                'Cloud and on-premise deployment support'
                ]
            },
            {
                title: 'SAP Support & Maintenance',
                detail: 'SAP Support & Maintenance',
                subpoints: [
                'Ongoing functional and technical support',
                'Performance tuning and issue resolution',
                'Security updates and system monitoring'
                ]
            },
            {
                title: 'SAP Custom Development',
                detail: 'SAP Custom Development',
                subpoints: [
                'Custom reports, modules, and enhancements using ABAP',
                'Workflow automation and Fiori app development',
                'Custom dashboards and analytics'
                ]
            },
            {
                title: 'SAP Consulting & Advisory',
                detail: 'SAP Consulting & Advisory',
                subpoints: [
                'SAP strategy and roadmap planning',
                'Business process analysis and optimization',
                'License management and compliance consulting'
                ]
            }
        ],
    },
    3: {
            title: 'Website Development Services',
            image: '/images/sap-integration.jpg',
            gallery: [
                '/assets/working-1.jpg',
                '/assets/app-1.jpg',
                '/assets/app-2.jpg',
                '/assets/app-3.jpg',
            ],
            points: [
                {
                    title: 'Custom Website Design & Development',
                    detail: 'Pixel-perfect, fully responsive designs tailored to your brand identity and goals.',
                },
                {
                    title: 'E-Commerce Solutions',
                    detail: 'Seamless online stores with secure payment integration, user-friendly catalogues, and backend management.',
                },
                {
                    title: 'Web Portals & Intranets',
                    detail: 'Feature-rich platforms for employee collaboration, customer self-service, and partner engagement.',
                },
                {
                    title: 'Progressive Web Apps (PWA)',
                    detail: 'Lightning-fast, app-like experiences that work across all devices.',
                },
                {
                    title: 'Search Engine Optimization (SEO) & Performance Optimization',
                    detail: 'Built-in SEO practices and page speed enhancements to help your site rank and perform.',
                },
                {
                    title: 'Cross Platform Mobile APP Development',
                    detail: 'With React Native, we create powerful, high-performance mobile apps for both iOS and Android using a single codebase. This reduces development time, ensures consistency across platforms, and saves you money.',
                }
            ],
    }
};

const Service = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const service = serviceDetails[id];

  if (!service) return <Typography>Service not found.</Typography>;

  return (
    <Container maxWidth="false" className='service-container'>
        <Box className='service-sub-container'>
            <Paper elevation={3}>
                {/* Top Image */}
                <Box className='service-detail' />

                {/* Content */}
                <Box p={3}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)} // Goes to previous page
                        sx={{ mb: 2 }}
                    >
                        Back to Services
                    </Button>
                    <Typography variant="h4" gutterBottom>
                        {service.title}
                    </Typography>
                    <Grid container className="service-grid" sx={{ marginTop: '30px' }}>
                        <Grid item xs={12} md={6} lg={6}>
                            {service.points.map((point, idx) => (
                                <Accordion key={`${idx}-${point.title}`} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography fontWeight={600}>{point.title}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                        {point.detail}
                                        </Typography>

                                        {point.subpoints && (
                                        <List dense disablePadding>
                                            {point.subpoints.map((sub, idx1) => (
                                            <ListItem key={`${idx1}-${sub}`}  sx={{ pl: 2 }}>
                                                <ListItemText primary={`• ${sub}`} />
                                            </ListItem>
                                            ))}
                                        </List>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} >
                            <Grid container className='gallary-container' spacing={2}>
                                {service.gallery.map((img, idx) => (
                                    <Grid item xs={6} key={idx}>
                                        <Box
                                            component="img"
                                            className='gallary-img'
                                            src={img}
                                            alt={`Gallery ${idx + 1}`}
                                            sx={{
                                                width: '100%',
                                                height: 120,
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    </Container>
  );
};

export default Service;
